import React, { useEffect, useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  findProductById,
  getProductCategory,
  isAccessory,
  formatZAR,
  getProductImages,
  loadProductDescription,
} from '../Components/ProductRetrieval'
import { useCart } from '../Components/Cart'

const Product = () => {
  const { productID } = useParams()
  const [selectedImage, setSelectedImage] = useState(0)

  useEffect(() => {
    setSelectedImage(0)
  }, [productID])

  const product = useMemo(() => findProductById(productID), [productID])

  const category = useMemo(
    () => (product ? getProductCategory(product.id) : null),
    [product],
  )

  const _isBoardGame = category === 'boardGame'
  const _isAccessory = useMemo(
    () => (product ? isAccessory(product.id) : false),
    [product],
  )
  const _isOnePiece = category === 'onePiece'
  const _isMTG = category === 'mtg'

  const backLink = _isBoardGame
    ? '/BoardGames'
    : _isAccessory
    ? '/TcgAccessories'
    : _isOnePiece || _isMTG
    ? '/Tcg'
    : '/'

 


  const images = useMemo(() => getProductImages(product), [product])

  const [description, setDescription] = useState('')

  useEffect(() => {
    setDescription('')
    if (!product) return
    loadProductDescription(product).then(setDescription)
  }, [product])

  const [justAdded, setJustAdded] = useState(false)
  const { addToCart } = useCart()

  const handleAddToCart = () => {
    if (!product) return
    const variant = product
    addToCart(variant.id, variant.name)
    setJustAdded(true)
    setTimeout(() => setJustAdded(false), 2000)
  }

  if (!product) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Product not found</h1>
          <Link
            to="/"
            className="mt-4 inline-block text-emerald-600 hover:text-emerald-700"
          >
            &larr; Back to Home
          </Link>
        </div>
      </div>
    )
  }

  const isOutOfStock = product?.inStock === 0
  const isPreorder = product?.inStock === 2

  const headerGradient = _isBoardGame
    ? 'bg-gradient-to-br from-emerald-700 via-teal-700 to-emerald-900'
    : 'bg-gradient-to-br from-indigo-700 via-purple-700 to-indigo-900'

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <section className={`border-b border-gray-200 px-4 py-14 text-white sm:px-6 lg:px-8 ${headerGradient}`}>
        <div className="mx-auto max-w-7xl">
          <Link
            to={backLink}
            className="text-sm font-semibold uppercase tracking-widest text-indigo-200 hover:text-indigo-100"
          >
            &larr; Back
          </Link>
          <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
            {product.name}
          </h1>
        </div>
      </section>

      {isPreorder && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="flex items-center gap-3 rounded-lg bg-amber-100 px-4 py-3">
              <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              <p className="text-sm font-medium text-amber-800">
                This product is on a pre-order basis only, it will take from 2-3 weeks before it will be shipped. We appreciate your patience in this matter.
              </p>
            </div>
          </div>
        </div>
      )}

      {isOutOfStock && (
        <div className="bg-gray-100 border-b border-gray-200 px-4 py-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="flex items-center gap-3 rounded-lg bg-gray-200 px-4 py-3">
              <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              <p className="text-sm font-medium text-gray-600">
                This product is currently out of stock.
              </p>
            </div>
          </div>
        </div>
      )}

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <div className={`flex aspect-square items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white ${isOutOfStock ? 'opacity-60' : ''}`}>
              {images[selectedImage] ? (
                <img
                  src={images[selectedImage].src}
                  alt={images[selectedImage].alt}
                  className={`h-full w-full object-contain p-4 ${isOutOfStock ? 'grayscale' : ''}`}
                />
              ) : (
                <span className="text-sm font-medium text-gray-400">
                  No image available
                </span>
              )}
            </div>

            {images.length > 1 && (
              <div className="mt-4 flex gap-3">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`h-20 w-20 overflow-hidden rounded-lg border-2 ${
                      idx === selectedImage ? 'border-emerald-600'
                        : 'border-gray-200'
                    }`}
                  >
                    <img
                      src={img.src}
                      alt={img.label}
                      className="h-full w-full object-contain"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <h2 className={`text-3xl font-bold ${isOutOfStock ? 'text-gray-500' : 'text-gray-900'}`}>
              {product.name}
            </h2>
            <p className={`mt-4 text-4xl font-bold ${isOutOfStock ? 'text-gray-400' :  _isAccessory ? 'text-indigo-700' : 'text-emerald-700'}`}>
              {formatZAR(product.price)}
            </p>

            

            {description && (
              <div className="mt-8">
                <h3 className={`text-lg font-semibold ${isOutOfStock ? 'text-gray-500' : 'text-gray-900'}`}>
                  Description
                </h3>
                <p className={`mt-2 whitespace-pre-line leading-relaxed ${isOutOfStock ? 'text-gray-400' : 'text-gray-600'}`}>
                  {description}
                </p>
              </div>
            )}

            {isOutOfStock ? (
              <button
                disabled
                className="mt-10 w-full cursor-not-allowed rounded-lg bg-gray-400 px-6 py-3 text-base font-semibold text-gray-200 sm:w-auto"
              >
                Out of stock
              </button>
            ) : (
              <button
                onClick={handleAddToCart}
                className={`mt-10 w-full rounded-lg px-6 py-3 text-base font-semibold text-white transition sm:w-auto ${
                  justAdded
                    ? _isAccessory ? 'bg-indigo-600' : 'bg-emerald-600'
                    : _isAccessory ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-emerald-700 hover:bg-emerald-800'
                }`}
              >
                {justAdded ? 'Added to cart' : 'Add to cart'}
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}

export default Product
